import React, { useState, useEffect } from 'react';
import { useAlert } from '../../../providers/AlertProvider';
import { useAuthProvider } from '../../../providers/AuthProvider';
import { validatePassword } from "../../../api/profiles";
import logger from "../../../utils/logger";

import RequestPassword from "../RequestPassword";
import "../../css/GlobalStyles.css";

const HOLIDAY_TYPE_LABELS = {
    annual_leave: 'Annual Leave',
    sick_leave: 'Sick Leave',
    emergency_leave: 'Emergency Leave',
    unpaid_leave: 'Unpaid Leave'
};

const HolidaysReport = ({
    holidaysData = [],
    usersData = {},
    selectedYear = new Date().getFullYear(),
    selectedDriverId = null,
    theme
}) => {
    const { profile } = useAuthProvider();
    const { showAlert } = useAlert();

    const [tableData, setTableData] = useState([]);
    const [summaryStats, setSummaryStats] = useState({});
    const [driverHolidayDates, setDriverHolidayDates] = useState([]);

    const isLightTheme = theme === 'light';

    const displayColumns = [
        { key: 'name', label: 'Name' },
        { key: 'user_type', label: 'Type' },
        { key: 'allocation', label: 'Annual Leave Allocation' },
        { key: 'remaining_annual_leave', label: 'Remaining Annual Leave' },
        { key: 'sick_days', label: 'Sick Days' },
        { key: 'emergency_leave', label: 'Emergency Leave' },
        { key: 'unpaid_leave', label: 'Unpaid Leave' },
        { key: 'total_holidays', label: 'Total Days Off' }
    ];

    // Request Password States =======================================================================================================
    const [openRequestPassword, setOpenRequestPassword] = useState(false);

    useEffect(() => {
        setOpenRequestPassword(true);
        //showData(); // for debugging
    }, []);
    
    const handlePasswordSubmission = async (password) => {
        const email = profile.email;
    
        try {
            const { status } = await validatePassword(password, email);
            if (status === 200) {
                showData();
            } else {
                logger.log("Invalid password.");
                showAlert({
                    message: 'Invalid password. Access denied.',
                    type: 'danger',
                    buttons: [{
                        type: 'danger',
                        label: 'Close',
                    }],
                });
            }
        } catch (error) {
            logger.error("Error validating password:", error);
            showAlert({
                message: 'An error occurred while validating the password.',
                type: 'danger',
                buttons: [{
                    type: 'danger',
                    label: 'Close',
                }],
            });
        }
        setOpenRequestPassword(false);
    };

    const showData = () => {
        console.log('HolidaysReport - showData called');
        console.log('HolidaysData:', holidaysData);
        console.log('UsersData:', usersData);
        
        if (!holidaysData || !usersData) {
            logger.log("No data available for holidays report");
            console.log('Missing data - returning early');
            return;
        }

        const overviewUsers = [
            ...(usersData.drivers || []).filter(user => user.holidays !== null && user.holidays !== undefined).map(user => ({ ...user, user_type: 'Driver' })),
            ...(usersData.admins || []).filter(user => user.holidays !== null && user.holidays !== undefined).map(user => ({ ...user, user_type: 'Admin' })),
            ...(usersData.warehouseStaff || []).filter(user => user.holidays !== null && user.holidays !== undefined).map(user => ({ ...user, user_type: 'Warehouse' }))
        ];

        const allUsers = selectedDriverId
            ? (usersData.drivers || [])
                .filter(user => String(user.id) === String(selectedDriverId))
                .map(user => ({ ...user, user_type: 'Driver' }))
            : overviewUsers;
        
        console.log('All users:', allUsers);

        // Calculate holiday statistics for each user
        const processedData = allUsers.map(user => {
            const userHolidays = holidaysData.filter(
                holiday => String(holiday.user_id) === String(user.id)
            );
            
            const annualLeaveCount = userHolidays.filter(h => h.holiday_type === 'annual_leave').length;
            const sickDaysCount = userHolidays.filter(h => h.holiday_type === 'sick_leave').length;
            const emergencyLeaveCount = userHolidays.filter(h => h.holiday_type === 'emergency_leave').length;
            const unpaidLeaveCount = userHolidays.filter(h => h.holiday_type === 'unpaid_leave').length;
            
            const userAllocation = Number(user.holidays ?? 20);
            
            return {
                name: `${user.name} ${user.surname}`,
                user_type: user.user_type,
                allocation: userAllocation,
                remaining_annual_leave: Math.max(0, userAllocation - annualLeaveCount),
                sick_days: sickDaysCount,
                emergency_leave: emergencyLeaveCount,
                unpaid_leave: unpaidLeaveCount,
                total_holidays: userHolidays.length,
                annual_leave_used: annualLeaveCount
            };
        });

        // Sort by user type (Drivers first, then Admins, then Warehouse) and then by name
        const sortedData = processedData.sort((a, b) => {
            if (a.user_type !== b.user_type) {
                const typeOrder = { 'Driver': 1, 'Admin': 2, 'Warehouse': 3 };
                return typeOrder[a.user_type] - typeOrder[b.user_type];
            }
            return a.name.localeCompare(b.name);
        });

        setTableData(sortedData);

        if (selectedDriverId) {
            setDriverHolidayDates(
                holidaysData
                    .filter(holiday => String(holiday.user_id) === String(selectedDriverId))
                    .sort((a, b) => String(a.date).localeCompare(String(b.date)))
            );
        } else {
            setDriverHolidayDates([]);
        }

        // Calculate summary statistics
        const totalUsers = sortedData.length;
        const driversCount = sortedData.filter(u => u.user_type === 'Driver').length;
        const adminsCount = sortedData.filter(u => u.user_type === 'Admin').length;
        const warehouseCount = sortedData.filter(u => u.user_type === 'Warehouse').length;
        const totalAnnualLeaveUsed = sortedData.reduce((sum, u) => sum + u.annual_leave_used, 0);
        const totalSickDays = sortedData.reduce((sum, u) => sum + u.sick_days, 0);
        const totalEmergencyLeave = sortedData.reduce((sum, u) => sum + u.emergency_leave, 0);
        const totalUnpaidLeave = sortedData.reduce((sum, u) => sum + u.unpaid_leave, 0);
        const usersAtAnnualLeaveLimit = sortedData.filter(u => u.remaining_annual_leave === 0).length;

        setSummaryStats({
            totalUsers,
            driversCount,
            adminsCount,
            warehouseCount,
            totalAnnualLeaveUsed,
            totalSickDays,
            totalEmergencyLeave,
            totalUnpaidLeave,
            usersAtAnnualLeaveLimit,
            averageAnnualLeaveUsed: totalUsers > 0 ? (totalAnnualLeaveUsed / totalUsers).toFixed(1) : 0
        });

        logger.log("Holidays report data processed successfully", { 
            totalUsers, 
            totalHolidays: holidaysData.length 
        });
    };

    // Styling for the table
    const tableStyles = {
        width: '100%',
        borderCollapse: 'collapse',
        marginTop: '20px',
        backgroundColor: isLightTheme ? '#ffffff' : '#2c2c2c',
        border: `1px solid ${isLightTheme ? '#dee2e6' : '#444'}`,
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    };

    const headerStyles = {
        backgroundColor: isLightTheme ? '#f8f9fa' : '#383838',
        color: isLightTheme ? '#495057' : '#ffffff',
        fontWeight: 'bold',
        padding: '12px 8px',
        textAlign: 'left',
        borderBottom: `2px solid ${isLightTheme ? '#dee2e6' : '#555'}`,
        fontSize: '14px',
        verticalAlign: 'middle'
    };

    const cellStyles = {
        padding: '12px 8px',
        borderBottom: `1px solid ${isLightTheme ? '#dee2e6' : '#444'}`,
        color: isLightTheme ? '#212529' : '#ffffff',
        fontSize: '13px',
        textAlign: 'left',
        verticalAlign: 'middle'
    };

    const getRowStyle = (index) => ({
        backgroundColor: index % 2 === 0 
            ? (isLightTheme ? '#ffffff' : '#2c2c2c')
            : (isLightTheme ? '#f8f9fa' : '#353535')
    });

    const getUserTypeStyle = (userType) => ({
        ...cellStyles,
        fontWeight: 'bold',
        color: userType === 'Driver' ? '#007bff' : userType === 'Admin' ? '#6f42c1' : '#28a745' // Green for warehouse
    });

    const getRemainingLeaveStyle = (remaining) => ({
        ...cellStyles,
        fontWeight: 'bold',
        color: remaining === 0 ? '#dc3545' : remaining <= 5 ? '#fd7e14' : '#28a745'
    });

    if (tableData.length === 0 && !openRequestPassword) {
        return (
            <div style={{ textAlign: 'center', padding: '40px' }}>
                <h3>No holidays data available</h3>
                <p>Please ensure holidays data is loaded properly.</p>
            </div>
        );
    }

    return (
        <div style={{ 
            padding: '20px', 
            backgroundColor: isLightTheme ? '#ffffff' : '#1a1a1a',
            color: isLightTheme ? '#333333' : '#ffffff',
            minHeight: '100vh'
        }}>
            {/* Request Password Modal */}
            <RequestPassword
                isOpen={openRequestPassword}
                setIsOpen={setOpenRequestPassword}
                returnData={handlePasswordSubmission}
                theme={theme}
            />

            {tableData.length > 0 && (
                <>
                    {/* Report Header */}
                    <div style={{ marginBottom: '30px' }}>
                        <h2 style={{ 
                            margin: '0 0 10px 0', 
                            color: isLightTheme ? '#2c3e50' : '#ffffff',
                            fontSize: '28px',
                            fontWeight: 'bold'
                        }}>
                            Holidays Report {selectedYear}
                        </h2>
                        <p style={{ 
                            margin: '0', 
                            color: isLightTheme ? '#6c757d' : '#cccccc',
                            fontSize: '16px'
                        }}>
                            {selectedDriverId
                                ? `${tableData[0].name}'s holiday dates and leave usage for ${selectedYear}`
                                : `Comprehensive overview of holiday usage and remaining entitlements for ${selectedYear}`}
                        </p>
                    </div>

                    {/* Summary Statistics */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '15px',
                        marginBottom: '30px'
                    }}>
                        <div style={{
                            padding: '15px',
                            backgroundColor: isLightTheme ? '#e3f2fd' : '#1e3a8a',
                            borderRadius: '8px',
                            textAlign: 'center'
                        }}>
                            <h3 style={{ margin: '0 0 5px 0', fontSize: '24px', color: isLightTheme ? '#1976d2' : '#ffffff' }}>
                                {summaryStats.totalUsers}
                            </h3>
                            <p style={{ margin: '0', fontSize: '14px', color: isLightTheme ? '#333' : '#cccccc' }}>Total Users</p>
                        </div>
                        <div style={{
                            padding: '15px',
                            backgroundColor: isLightTheme ? '#e8f5e8' : '#166534',
                            borderRadius: '8px',
                            textAlign: 'center'
                        }}>
                            <h3 style={{ margin: '0 0 5px 0', fontSize: '24px', color: isLightTheme ? '#2e7d32' : '#ffffff' }}>
                                {summaryStats.totalAnnualLeaveUsed}
                            </h3>
                            <p style={{ margin: '0', fontSize: '14px', color: isLightTheme ? '#333' : '#cccccc' }}>Annual Leave Days Used</p>
                        </div>
                        <div style={{
                            padding: '15px',
                            backgroundColor: isLightTheme ? '#fff3e0' : '#ea580c',
                            borderRadius: '8px',
                            textAlign: 'center'
                        }}>
                            <h3 style={{ margin: '0 0 5px 0', fontSize: '24px', color: isLightTheme ? '#e65100' : '#ffffff' }}>
                                {summaryStats.totalSickDays}
                            </h3>
                            <p style={{ margin: '0', fontSize: '14px', color: isLightTheme ? '#333' : '#cccccc' }}>Total Sick Days</p>
                        </div>
                        <div style={{
                            padding: '15px',
                            backgroundColor: isLightTheme ? '#fce4ec' : '#be185d',
                            borderRadius: '8px',
                            textAlign: 'center'
                        }}>
                            <h3 style={{ margin: '0 0 5px 0', fontSize: '24px', color: isLightTheme ? '#ad1457' : '#ffffff' }}>
                                {summaryStats.usersAtAnnualLeaveLimit}
                            </h3>
                            <p style={{ margin: '0', fontSize: '14px', color: isLightTheme ? '#333' : '#cccccc' }}>Users at Leave Limit</p>
                        </div>
                    </div>

                    {/* Main Data Table */}
                    <table style={tableStyles}>
                        <thead>
                            <tr>
                                {displayColumns.map((column) => (
                                    <th key={column.key} style={headerStyles}>
                                        {column.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {tableData.map((row, index) => (
                                <tr key={index} style={getRowStyle(index)}>
                                    <td style={cellStyles}>{row.name}</td>
                                    <td style={getUserTypeStyle(row.user_type)}>{row.user_type}</td>
                                    <td style={{...cellStyles, fontWeight: 'bold', textAlign: 'center'}}>
                                        {row.allocation} days
                                    </td>
                                    <td style={getRemainingLeaveStyle(row.remaining_annual_leave)}>
                                        {row.remaining_annual_leave} / {row.allocation} days
                                    </td>
                                    <td style={cellStyles}>{row.sick_days}</td>
                                    <td style={cellStyles}>{row.emergency_leave}</td>
                                    <td style={cellStyles}>{row.unpaid_leave}</td>
                                    <td style={{...cellStyles, fontWeight: 'bold'}}>
                                        {row.total_holidays}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {selectedDriverId && (
                        <div style={{ marginTop: '35px' }}>
                            <h3 style={{ marginBottom: '5px' }}>
                                Holiday dates taken in {selectedYear}
                            </h3>
                            <p style={{
                                marginTop: 0,
                                color: isLightTheme ? '#6c757d' : '#cccccc'
                            }}>
                                {driverHolidayDates.length} day{driverHolidayDates.length === 1 ? '' : 's'} recorded
                            </p>

                            {driverHolidayDates.length > 0 ? (
                                <table style={tableStyles}>
                                    <thead>
                                        <tr>
                                            <th style={headerStyles}>Date</th>
                                            <th style={headerStyles}>Leave Type</th>
                                            <th style={headerStyles}>Notes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {driverHolidayDates.map((holiday, index) => (
                                            <tr key={holiday.id || `${holiday.date}-${index}`} style={getRowStyle(index)}>
                                                <td style={cellStyles}>
                                                    {new Date(`${holiday.date}T00:00:00`).toLocaleDateString('en-GB', {
                                                        weekday: 'short',
                                                        day: '2-digit',
                                                        month: 'short',
                                                        year: 'numeric'
                                                    })}
                                                </td>
                                                <td style={cellStyles}>
                                                    {HOLIDAY_TYPE_LABELS[holiday.holiday_type] || holiday.holiday_type || 'Not specified'}
                                                </td>
                                                <td style={cellStyles}>{holiday.notes || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div style={{
                                    padding: '20px',
                                    border: `1px solid ${isLightTheme ? '#dee2e6' : '#444'}`,
                                    borderRadius: '8px',
                                    backgroundColor: isLightTheme ? '#f8f9fa' : '#2c2c2c'
                                }}>
                                    No holidays are recorded for this driver in {selectedYear}.
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default HolidaysReport;